import DOM from '../DOM/DOM'

import { is } from '../is/is'

import log from '../Logger/log'

import type { IFullscreen } from './definitions'
import { EFullscreenEvent } from './definitions'

type TRequestFullScreen = (options?: FullscreenOptions | undefined) => Promise<void>

let lockTimer: number | null = null

/**
 * @see {@link https://github.com/google/closure-library/blob/master/closure/goog/dom/fullscreen.js}
 * @see {@link https://www.sitepoint.com/use-html5-full-screen-api/}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Element/requestFullScreen}
 */
const fullscreen: IFullscreen = {
  isInFullScreen: (): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const docEl = document as any

    return Boolean(
      /* eslint-disable @typescript-eslint/no-unsafe-member-access */
      docEl.webkitIsFullScreen ||
      docEl.mozFullScreen ||
      docEl.msFullscreenElement ||
      docEl.fullscreenElement
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    )
  },

  requestFullScreen: (isEnter = false): void => {
    log('[FULLSCREEN] request fullscreen.')

    // Az exitFullscreen a document-et használja...
    // a requestFullscreen pedig a document.documentElement-et
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const docEl = isEnter ? (document.documentElement as any) : document

    const method: TRequestFullScreen = isEnter
      ? (
        /* eslint-disable @typescript-eslint/no-unsafe-member-access */
        docEl.requestFullscreen ||
        docEl.mozRequestFullScreen ||
        docEl.webkitRequestFullscreen ||
        docEl.msRequestFullscreen
      )
      : (
        docEl.exitFullscreen ||
        docEl.mozCancelFullScreen ||
        docEl.webkitExitFullscreen ||
        docEl.msExitFullscreen
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
      )

    // https://caniuse.com/fullscreen
    //
    // Safari doesn't support stacking, meaning only one element can be set to full screen. webkitRequestFullScreen() is ignored for other elements and no error event is dispatched.
    if (!is.function(method)) {
      log(`[FULLSCREEN] A böngésző nem támogatja a(z) ${ isEnter ? 'request' : 'exit' }FullScreen-t.`)

      return
    }

    // eslint-disable-next-line no-restricted-syntax -- Itt megengedett.
    method.call(
      docEl,
      isEnter
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ? { navigationUI: 'hide' }
        : undefined
    )
      .then(() => {
        log(`[FULLSCREEN] ${ isEnter ? 'On' : 'Off' }.`)

        // Mivel a 'fullscreenchange' event nem crossbrowser, ezért sajátot lövünk ki:
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/fullscreenchange_event
        DOM.dispatch(document, EFullscreenEvent.Changed, false, isEnter)
      })
      .catch((error: Error) => {
        const zoom = isEnter ? 'into' : 'out from'

        log(
          `
            [FULLSCREEN] An error occurred while trying to switch ${ zoom } full-screen mode:
            ${ error.message } (${ error.name })
          `
        )
      })
  },

  toggleFullScreen: (lockDuration?: number): void => {
    if (is.number(lockTimer)) {
      return
    }

    const isInFullScreen = fullscreen.isInFullScreen()

    fullscreen.requestFullScreen(!isInFullScreen)

    if (is.undefined(lockDuration)) {
      return
    }

    const newLockTimer = window.setTimeout(() => {
      window.clearTimeout(newLockTimer)

      lockTimer = null
    }, lockDuration)

    lockTimer = newLockTimer
  },

  isSupported: (): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Itt megengedett.
    const doc = document as any

    const isSupported = (
      /* eslint-disable @typescript-eslint/no-unsafe-member-access */
      doc.fullscreenEnabled ||
      doc.mozFullscreenEnabled ||
      doc.webkitFullscreenEnabled ||
      doc.msFullscreenEnabled
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    )

    return is.boolean(isSupported)
      ? isSupported
      : false
  },

  off: (): void => {
    if (fullscreen.isInFullScreen()) {
      fullscreen.requestFullScreen(false)
    }
  },

  on: (): void => {
    if (!fullscreen.isInFullScreen()) {
      fullscreen.requestFullScreen(true)
    }
  }
}

export default fullscreen
